import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { AuthLayout } from '@/components/layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { accountApi } from '@/resources/account';
import { LoginParams } from '@/types';
import { loginSchema } from '@/schemas';
import { RoutePath } from '@/routes';
import { cn } from '@/lib/utils';

// AUT-39: WEB-003. `/login` inside the shared AuthLayout (WEB-001) --
// components/account.md § "/login -- three changes beyond the restyle":
// (1) no sign-up affordance anywhere on this screen (FR-5, J1's guardrail),
// (2) `Forgot password?` is a Link on the password label row, not a ghost
// Button, and (3) validation fires on submit rather than being gated behind
// `touchedFields`. Everything else -- the `remember` boolean, the pending
// spinner, the incorrect-credentials toast raised by useLogin's onError --
// is preserved unchanged (FR-8).
const Login = () => {
  const { mutate: login, isPending: isLoginPending } = accountApi.useLogin();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<LoginParams>({
    resolver: zodResolver(loginSchema),
    defaultValues: { remember: false },
  });

  const remember = useWatch({ control, name: 'remember' });

  const onSubmit = (data: LoginParams) => {
    login({ email: data.email, password: data.password, remember: data.remember || false });
  };

  return (
    <AuthLayout variant="hero">
      {/* WEB-008: made literal -- the page heading is an <h2>, the hero copy
          in AuthLayout is the product's only <h1> (correction sheet § 1). */}
      <h2 className="text-2xl font-semibold text-foreground">Sign in</h2>
      <p className="mt-2 text-sm text-muted-foreground">Enter your details to access your Signal workspace.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6" noValidate>
        <div className="w-full space-y-2">
          <Label
            htmlFor="email"
            className={cn('block text-sm leading-none', errors.email && 'text-destructive')}
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@brand.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={cn(errors.email && 'border-destructive focus-visible:ring-destructive')}
            {...register('email')}
          />
          {errors.email && (
            <p id="email-error" className="leading-none text-destructive text-sm">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="w-full space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Label
              htmlFor="password"
              className={cn('block text-sm leading-none', errors.password && 'text-destructive')}
            >
              Password
            </Label>
            <Link
              to={RoutePath.ForgotPassword}
              className="text-sm font-medium text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
            className={cn(errors.password && 'border-destructive focus-visible:ring-destructive')}
            {...register('password')}
          />
          {errors.password && (
            <p id="password-error" className="leading-none text-destructive text-sm">
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="remember"
            checked={remember}
            onCheckedChange={(checked) => setValue('remember', !!checked)}
          />
          <label htmlFor="remember" className="cursor-pointer text-sm font-normal text-muted-foreground">
            Keep me signed in
          </label>
        </div>

        <Button type="submit" className="w-full" disabled={isLoginPending}>
          {isLoginPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
